import React from "react";

// Custom Link Modal Component
export function LinkModal({
  isOpen,
  onClose,
  onSave,
  initialUrl = "",
  initialTarget = "_self",
  tabInfo = []
}) {
  const [url, setUrl] = React.useState(initialUrl);
  const [target, setTarget] = React.useState(initialTarget);
  const [anchorType, setAnchorType] = React.useState("url"); // 'url', 'tab', 'custom'
  const [customAnchor, setCustomAnchor] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState("");

  React.useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
      setTarget(initialTarget);
      // Check if it's an anchor link
      if (initialUrl && initialUrl.startsWith("#")) {
        // Check if it's a known tab anchor
        const cleanAnchor = initialUrl.substring(1); // Remove #
        const matchingTab = tabInfo.find((tab) => tab.id === cleanAnchor);

        if (matchingTab) {
          setAnchorType("tab");
          setSelectedTab(cleanAnchor);
          setUrl("");
          setCustomAnchor("");
        } else {
          setAnchorType("custom");
          setCustomAnchor(initialUrl);
          setUrl("");
          setSelectedTab("");
        }
      } else {
        setAnchorType("url");
        setCustomAnchor("");
        setSelectedTab("");
      }
    }
  }, [isOpen, initialUrl, initialTarget, tabInfo]);

  function handleSave() {
    let finalUrl = url;
    let finalTarget = target;

    if (anchorType === "tab" && selectedTab) {
      finalUrl = `#${selectedTab}`;
      finalTarget = "_self";
    } else if (anchorType === "custom" && customAnchor) {
      finalUrl = customAnchor.startsWith("#")
        ? customAnchor
        : `#${customAnchor}`;
      finalTarget = "_self";
    }

    onSave(finalUrl, finalTarget);
    onClose();
  }

  if (!isOpen) return null;

  return React.createElement(
    "div",
    {
      className: "puck-link-modal-overlay",
      onClick: (e) => {
        if (e.target === e.currentTarget) onClose();
      }
    },
    React.createElement(
      "div",
      { className: "puck-link-modal" },
      React.createElement(
        "div",
        { className: "puck-link-modal-header" },
        React.createElement(
          "h5",
          { className: "modal-title" },
          "Insert/Edit Link"
        ),
        React.createElement(
          "button",
          {
            type: "button",
            className: "btn-close",
            onClick: onClose,
            "aria-label": "Close"
          },
          React.createElement("i", { className: "fas fa-times" })
        )
      ),
      React.createElement(
        "div",
        { className: "puck-link-modal-body" },
        // Link Type Selection
        React.createElement(
          "div",
          { className: "form-group mb-3" },
          React.createElement(
            "label",
            { className: "form-label" },
            "Link Type"
          ),
          React.createElement(
            "div",
            { className: "btn-group w-100" },
            React.createElement(
              "button",
              {
                type: "button",
                className: `btn btn-outline-secondary ${
                  anchorType === "url" ? "active" : ""
                }`,
                onClick: () => setAnchorType("url")
              },
              "URL"
            ),
            React.createElement(
              "button",
              {
                type: "button",
                className: `btn btn-outline-secondary ${
                  anchorType === "tab" ? "active" : ""
                }`,
                onClick: () => setAnchorType("tab")
              },
              "Tab Anchor"
            ),
            React.createElement(
              "button",
              {
                type: "button",
                className: `btn btn-outline-secondary ${
                  anchorType === "custom" ? "active" : ""
                }`,
                onClick: () => setAnchorType("custom")
              },
              "Custom Anchor"
            )
          )
        ),

        // URL Input (shown for URL type)
        anchorType === "url" &&
          React.createElement(
            "div",
            { className: "form-group mb-3" },
            React.createElement("label", { className: "form-label" }, "URL"),
            React.createElement("input", {
              type: "text",
              className: "form-control",
              value: url,
              onChange: (e) => setUrl(e.target.value),
              placeholder: "https://example.com"
            })
          ),

        // Tab Selection (shown for tab anchor type)
        anchorType === "tab" &&
          React.createElement(
            "div",
            { className: "form-group mb-3" },
            React.createElement(
              "label",
              { className: "form-label" },
              "Select Tab"
            ),
            React.createElement(
              "select",
              {
                className: "form-control",
                value: selectedTab,
                onChange: (e) => setSelectedTab(e.target.value)
              },
              React.createElement("option", { value: "" }, "Choose a tab..."),
              tabInfo.map((tab) =>
                React.createElement(
                  "option",
                  {
                    key: tab.id,
                    value: tab.id
                  },
                  tab.title
                )
              )
            ),
            React.createElement(
              "small",
              { className: "form-text text-muted" },
              "Creates a link to navigate to the selected tab on this page"
            )
          ),

        // Custom Anchor Input (shown for custom anchor type)
        anchorType === "custom" &&
          React.createElement(
            "div",
            { className: "form-group mb-3" },
            React.createElement(
              "label",
              { className: "form-label" },
              "Anchor Name"
            ),
            React.createElement("input", {
              type: "text",
              className: "form-control",
              value: customAnchor,
              onChange: (e) => setCustomAnchor(e.target.value),
              placeholder: "#section-name or section-name"
            }),
            React.createElement(
              "small",
              { className: "form-text text-muted" },
              "Enter the anchor name with or without #"
            )
          ),

        // Target Selection (only for URL type)
        anchorType === "url" &&
          React.createElement(
            "div",
            { className: "form-group mb-3" },
            React.createElement(
              "label",
              { className: "form-label" },
              "Open Link In"
            ),
            React.createElement(
              "select",
              {
                className: "form-control",
                value: target,
                onChange: (e) => setTarget(e.target.value)
              },
              React.createElement("option", { value: "_self" }, "Same Window"),
              React.createElement(
                "option",
                { value: "_blank" },
                "New Tab/Window"
              )
            )
          )
      ),
      React.createElement(
        "div",
        { className: "puck-link-modal-footer" },
        React.createElement(
          "button",
          {
            type: "button",
            className: "btn btn-secondary",
            onClick: onClose
          },
          "Cancel"
        ),
        React.createElement(
          "button",
          {
            type: "button",
            className: "btn btn-primary",
            onClick: handleSave,
            disabled:
              (anchorType === "url" && !url) ||
              (anchorType === "tab" && !selectedTab) ||
              (anchorType === "custom" && !customAnchor)
          },
          "Save"
        )
      )
    )
  );
}
